import { currentIdToken } from './auth';
import type { PortalConfig } from './config';
import type {
  AppInput,
  CodeFinding,
  CodeSnippet,
  Dismissal,
  Integration,
  OnboardedApp,
  Overview,
  Pentest,
  PentestFinding,
  PentestRun,
  ScannedRepository,
  Viewer,
} from './types';

/**
 * The API is served from the same CloudFront domain under /api, so these are
 * same-origin requests and there is no CORS preflight.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(config: PortalConfig, path: string, init?: RequestInit): Promise<T> {
  const token = currentIdToken();

  if (!token) {
    throw new ApiError(401, 'Your session has expired. Reload the page to sign in again.');
  }

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...init?.headers,
      authorization: `Bearer ${token}`,
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(response.status, body.message ?? `Request failed (HTTP ${response.status}).`);
  }

  // A successful DELETE comes back as 204 with no body.
  if (response.status === 204) return undefined as T;

  // CloudFront rewrites 404s to /index.html with a 200 status so the SPA can do its
  // own routing, which means an unmatched API route arrives here as a perfectly
  // successful page of HTML. Parsing it reports "Unexpected token '<'", which says
  // nothing about the cause, so check the type and name the real problem instead.
  if (!(response.headers.get('content-type') ?? '').includes('application/json')) {
    throw new ApiError(
      404,
      `The API did not handle ${init?.method ?? 'GET'} ${path}, so the portal received the app shell instead of data. The route is probably missing or the path is malformed.`,
    );
  }

  return (await response.json()) as T;
}

function body(payload: unknown): RequestInit {
  return { body: JSON.stringify(payload) };
}

export const api = {
  overview: (config: PortalConfig) => request<Overview>(config, '/overview'),

  me: (config: PortalConfig) => request<Viewer>(config, '/me'),

  apps: (config: PortalConfig) => request<{ apps: OnboardedApp[] }>(config, '/apps'),

  onboardApp: (config: PortalConfig, input: AppInput) =>
    request<OnboardedApp>(config, '/apps', { method: 'POST', ...body(input) }),

  updateApp: (config: PortalConfig, input: AppInput) =>
    request<OnboardedApp>(config, `/apps/${encodeURIComponent(input.slug)}`, { method: 'PUT', ...body(input) }),

  removeApp: (config: PortalConfig, slug: string) =>
    request<void>(config, `/apps/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  repositories: (config: PortalConfig) => request<{ repositories: ScannedRepository[] }>(config, '/repositories'),

  integrations: (config: PortalConfig) => request<{ integrations: Integration[] }>(config, '/integrations'),

  codeFindings: (config: PortalConfig, filters: { repository?: string; severity?: string; status?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])),
    );

    return request<{ findings: CodeFinding[] }>(config, `/code-findings${query.size ? `?${query}` : ''}`);
  },

  codeSnippets: (config: PortalConfig, findingArns: string[]) =>
    request<{ snippets: CodeSnippet[] }>(config, '/code-findings/snippets', {
      method: 'POST',
      ...body({ findingArns }),
    }),

  dismissals: (config: PortalConfig) =>
    request<{ dismissals: Dismissal[] }>(config, '/code-findings/dismissals'),

  dismissFinding: (config: PortalConfig, findingArn: string, reason: string) =>
    request<Dismissal>(config, '/code-findings/dismissals', { method: 'POST', ...body({ findingArn, reason }) }),

  restoreFinding: (config: PortalConfig, filterArn: string) =>
    request<void>(config, `/code-findings/dismissals?filterArn=${encodeURIComponent(filterArn)}`, {
      method: 'DELETE',
    }),

  pentests: (config: PortalConfig) => request<{ pentests: Pentest[] }>(config, '/pentests'),

  pentestRuns: (config: PortalConfig, pentestId: string) =>
    request<{ runs: PentestRun[] }>(config, `/pentests/${encodeURIComponent(pentestId)}/runs`),

  pentestFindings: (config: PortalConfig, pentestId: string, jobId?: string) =>
    request<{ findings: PentestFinding[] }>(
      config,
      `/pentests/${encodeURIComponent(pentestId)}/findings${jobId ? `?jobId=${encodeURIComponent(jobId)}` : ''}`,
    ),

  startPentestRun: (config: PortalConfig, pentestId: string) =>
    request<PentestRun>(config, `/pentests/${encodeURIComponent(pentestId)}/runs`, { method: 'POST' }),

  startRepositoryScan: (config: PortalConfig, projectId: string) =>
    request<{ scanId?: string; status?: string }>(config, `/repositories/${encodeURIComponent(projectId)}/scans`, {
      method: 'POST',
    }),
};
